/**
 * At-Home add-on cart gate — KAN-316 (theme layer).
 *
 * Keeps gated add-ons (any cart line carrying the private `_athome_gated_addon`
 * line-item property — e.g. the $35 "Add on Custom Scent Virtual Consultation")
 * out of the cart unless a gate product — an At-Home experience (Base / Premier
 * / Concierge) — is also in the cart.
 *
 * Loaded GLOBALLY (rendered next to the cart drawer in layout/theme.liquid) so
 * the auto-remove reconciliation runs wherever the cart can be edited — not only
 * on the At-Home product pages. Without that, a customer could add both on the
 * PDP, navigate away, remove the At-Home experience from the drawer on another
 * page, and keep an orphaned add-on.
 *
 * Two prevent-add UX modes, chosen via config `mode`:
 *   - "disable" — the add-on "add" buttons are disabled with a note until a gate
 *     product is in the cart.
 *   - "modal" — the add-on buttons stay enabled; attempting to add one without a
 *     gate product opens a modal whose "Add both to cart" CTA adds the selected
 *     At-Home tier + the add-on together.
 * Add-on forms only exist on the At-Home pages, so prevent-add is inherently
 * scoped there; only the auto-remove half is global.
 *
 * SAFETY MODEL (this code can remove cart lines, so it fails safe by design):
 *   1. Property-scoped — ONLY lines carrying `_athome_gated_addon` are ever
 *      removed. That property is stamped exclusively by this feature's own
 *      add-on forms, so no normally-added product can be touched.
 *   2. Fail-closed on incomplete config — if fewer gate product ids resolve than
 *      `expectedGateCount` (e.g. a product handle changed), auto-remove is
 *      disabled entirely rather than removing against a partial gate list.
 *   3. Kill switch — `autoRemove: false` disables removal without a code change.
 *   4. Fail-open on blocking — when config is incomplete, or before the cart is
 *      known, add-on buttons are NOT locked, so the gate can never block a sale.
 *   5. Section ids are UNIONed in — extra gate ids can only reduce removals.
 *   6. No removal without a successful /cart.js read.
 *   7. No false success — a removal or add is only reported to the customer
 *      after the server response confirms it. Failures surface a message.
 *
 * This is the graceful UX layer. It is NOT authoritative: it cannot stop a
 * hand-crafted /cart/add + /checkout. A Shopify cart_checkout_validation
 * Function (KAN-337) is the server-side enforcement — it is not deployed yet.
 */
class AtHomeAddonGate extends HTMLElement {
  connectedCallback() {
    this.config = this.readConfig();
    this.addonProperty = this.config.addonProperty || '_athome_gated_addon';
    this.mode = this.config.mode === 'modal' ? 'modal' : 'disable';
    this.expectedGateCount = Number(this.config.expectedGateCount) || 0;
    this.autoRemoveEnabled = this.config.autoRemove !== false;

    // 'unknown' until the first /cart.js read resolves. Treated as permissive so
    // a submit landing in that window is never wrongly blocked (it is resolved
    // against a fresh cart read first — see onFormSubmit).
    this.gateState = 'unknown';
    this.reconciling = false;
    this.pendingAddon = null;
    this.bypassForm = null;

    this.boundReconcile = () => this.reconcile();
    ['cart:refreshed', 'customAddToCartComplete'].forEach((evt) => {
      document.addEventListener(evt, this.boundReconcile);
    });

    // Intercept a gated add-on submit before the <product-form> handles it.
    this.boundSubmit = this.onFormSubmit.bind(this);
    document.addEventListener('submit', this.boundSubmit, true);

    if (this.mode === 'modal') this.setupModal();

    this.flushRememberedNotice();
    this.reconcile();
  }

  disconnectedCallback() {
    ['cart:refreshed', 'customAddToCartComplete'].forEach((evt) => {
      document.removeEventListener(evt, this.boundReconcile);
    });
    document.removeEventListener('submit', this.boundSubmit, true);
  }

  readConfig() {
    try {
      return JSON.parse(this.querySelector('[data-athome-gate-config]').textContent);
    } catch (e) {
      return {};
    }
  }

  /**
   * Gate product ids: the globally-resolved list, UNIONed with any ids the
   * At-Home section resolved from its own theme-editor settings (so an admin who
   * repoints a tier still has it counted). Union can only ADD gate products,
   * which can only reduce removals — a fail-safe direction.
   */
  gateProductIds() {
    const ids = new Set((this.config.gateProductIds || []).map(String));
    document.querySelectorAll('[data-athome-gate-section-ids]').forEach((el) => {
      try {
        JSON.parse(el.textContent).forEach((id) => ids.add(String(id)));
      } catch (e) {
        /* ignore malformed section payload */
      }
    });
    return [...ids].filter(Boolean);
  }

  /**
   * Auto-remove is allowed only with a complete, enabled gate configuration.
   * Removing against a partial gate list could delete a legitimately-paired
   * add-on (e.g. if the Premier handle changed and dropped out of the list).
   */
  canAutoRemove(gateIds) {
    if (!this.autoRemoveEnabled) return false;
    if (!gateIds.length) return false;
    if (this.expectedGateCount && gateIds.length < this.expectedGateCount) return false;
    return true;
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  hasGateProduct(cart, gateIds) {
    return (cart?.items || []).some((item) => gateIds.includes(String(item.product_id)));
  }

  gatedAddonLines(cart) {
    return (cart?.items || []).filter(
      (item) => item?.properties && item.properties[this.addonProperty]
    );
  }

  async reconcile() {
    if (this.reconciling) return;
    this.reconciling = true;
    try {
      const cart = await this.fetchCart();
      if (!cart) return; // never act on an unknown cart state

      const gateIds = this.gateProductIds();

      // Fail-open: with an incomplete/disabled config we neither remove nor lock.
      // The checkout-validation Function remains the authoritative enforcement.
      if (!this.canAutoRemove(gateIds)) {
        this.gateState = 'open';
        if (this.mode === 'disable') this.updateAddButtons(true);
        this.syncDrawerCount(cart.item_count);
        return;
      }

      const gateInCart = this.hasGateProduct(cart, gateIds);
      this.gateState = gateInCart ? 'open' : 'closed';

      let removalAttempted = false;
      if (!gateInCart) {
        const orphans = this.gatedAddonLines(cart);
        if (orphans.length) {
          removalAttempted = true;
          const ok = await this.removeLines(orphans);
          // Persist first: the cart page reloads on a cart change, which would
          // destroy the toast and remove the add-on silently.
          this.rememberNotice(ok ? this.config.noticeText : this.config.removeFailedText);
          this.showToast(ok ? this.config.noticeText : this.config.removeFailedText);
        }
      }

      // Keep the cart drawer's "Your cart N" heading counter in sync. The theme's
      // renderContents only re-renders #CartDrawer-Body, not the heading, so we
      // mirror what cart.js does.
      if (removalAttempted) {
        this.scheduleCountResync();
      } else {
        this.syncDrawerCount(cart.item_count);
      }

      if (this.mode === 'disable') this.updateAddButtons(this.gateState === 'open');
    } finally {
      this.reconciling = false;
    }
  }

  /**
   * Returns true ONLY when the server confirms the lines are gone. A failed
   * request (or a response that still contains a gated add-on) returns false so
   * the caller never tells the customer something was removed when it was not.
   */
  async removeLines(lines) {
    const updates = lines.reduce((acc, line) => {
      acc[line.key] = 0;
      return acc;
    }, {});
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'],
        }),
      });
      if (!res.ok) return false;
      const json = await res.json();
      this.renderDrawer(json);
      this.syncDrawerCount(json.item_count);
      // Confirm against the returned cart rather than trusting the status code.
      return this.gatedAddonLines(json).length === 0;
    } catch (e) {
      return false;
    }
  }

  // The cart drawer's "Your cart N" heading counter lives outside #CartDrawer-Body,
  // so cart-drawer.renderContents() never updates it. Mirror the theme (cart.js).
  syncDrawerCount(count) {
    if (count === null || count === undefined) return;
    document.querySelectorAll('.cart-drawer__title-counter').forEach((el) => {
      el.innerHTML = count;
    });
  }

  async syncCountFromServer() {
    const cart = await this.fetchCart();
    if (cart) this.syncDrawerCount(cart.item_count);
  }

  /**
   * Removing a line can cascade: the gift-with-purchase engine removes its gift
   * when the cart drops below the threshold, and it does not update the drawer
   * heading. Re-read the authoritative count a few times while those settle so
   * the heading cannot be left showing a stale number after our removal.
   */
  scheduleCountResync() {
    [400, 1200, 2500].forEach((ms) => setTimeout(() => this.syncCountFromServer(), ms));
  }

  // ---- disable mode ----
  updateAddButtons(gateOpen) {
    document.querySelectorAll('form[data-athome-addon-gated]').forEach((form) => {
      form.classList.toggle('is-gate-locked', !gateOpen);
      const btn = form.querySelector('button[name="add"], button[type="submit"]');
      if (btn) {
        btn.disabled = !gateOpen;
        btn.setAttribute('aria-disabled', String(!gateOpen));
      }
    });
    document.querySelectorAll('[data-athome-gate-note]').forEach((note) => {
      note.hidden = gateOpen;
    });
  }

  // ---- submit intercept (both modes) ----
  onFormSubmit(event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[data-athome-addon-gated]')) return;

    // A submit we re-dispatched after confirming the gate — let it through.
    if (this.bypassForm === form) {
      this.bypassForm = null;
      return;
    }
    if (this.gateState === 'open') return; // gate satisfied

    event.preventDefault();
    event.stopImmediatePropagation();

    // The cart is not known yet (first /cart.js still in flight). Resolve against
    // a fresh read before deciding, so an early click is never wrongly blocked.
    if (this.gateState === 'unknown') {
      this.resolveThenRetry(form);
      return;
    }
    this.blockAdd(form);
  }

  async resolveThenRetry(form) {
    await this.reconcile();
    if (this.gateState === 'open') {
      this.bypassForm = form;
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      return;
    }
    this.blockAdd(form);
  }

  blockAdd(form) {
    if (this.mode === 'modal') {
      const variantInput = form.querySelector('input[name="id"]');
      const nameEl = form.querySelector('.pdp-addon-copy h3');
      this.openModal(variantInput ? variantInput.value : null, nameEl ? nameEl.textContent.trim() : '');
      return;
    }
    this.showToast(this.config.lockText || 'Add an At-Home experience to your cart first.');
  }

  // ---- modal mode ----
  setupModal() {
    this.modal = document.querySelector('[data-athome-gate-modal]');
    if (!this.modal) return;
    // Reparent to <body> so the fixed modal escapes any transformed ancestor
    // stacking context (the PDP's .pdp-info-track has a transform) and can stack
    // above the cart drawer overlay.
    if (this.modal.parentElement !== document.body) document.body.appendChild(this.modal);
    this.modalError = this.modal.querySelector('[data-gate-modal-error]');
    this.modal.querySelectorAll('[data-gate-modal-close]').forEach((b) =>
      b.addEventListener('click', () => this.closeModal())
    );
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modalOpen) this.closeModal();
    });
    this.addBothBtn = this.modal.querySelector('[data-gate-add-both]');
    if (this.addBothBtn) this.addBothBtn.addEventListener('click', () => this.addBoth());
  }

  setModalError(message) {
    if (!this.modalError) return;
    this.modalError.textContent = message || '';
    this.modalError.hidden = !message;
  }

  openModal(addonVariant, addonName) {
    if (!this.modal) return;
    this.pendingAddon = addonVariant;
    const nameSlot = this.modal.querySelector('[data-gate-addon-name]');
    if (nameSlot) nameSlot.textContent = addonName || 'This add-on';
    this.setModalError('');
    this.lastFocus = document.activeElement;
    this.modal.hidden = false;
    this.modalOpen = true;
    document.documentElement.classList.add('athome-gate-modal-open');
    const focusEl = this.addBothBtn || this.modal.querySelector('button');
    if (focusEl) focusEl.focus();
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    this.modalOpen = false;
    document.documentElement.classList.remove('athome-gate-modal-open');
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  }

  /**
   * Adds the selected At-Home tier + the pending add-on together. The modal stays
   * open with a message if the add does not succeed, so the customer is never
   * left believing items were added when they were not.
   */
  async addBoth() {
    const tier = document.querySelector('[data-tier].selected') || document.querySelector('[data-tier]');
    const tierVariant = tier ? tier.dataset.variantId : null;
    const items = [];
    if (tierVariant) items.push({ id: Number(tierVariant), quantity: 1 });
    if (this.pendingAddon) {
      items.push({ id: Number(this.pendingAddon), quantity: 1, properties: { [this.addonProperty]: '1' } });
    }
    if (items.length < 2) {
      this.setModalError(this.config.addFailedText);
      return;
    }
    if (this.addBothBtn) this.addBothBtn.disabled = true;
    this.setModalError('');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'] }),
      });
      if (!res.ok) {
        this.setModalError(this.config.addFailedText);
        return;
      }
      const json = await res.json();
      this.renderDrawer(json);
      this.closeModal();
      this.openDrawer();
      document.dispatchEvent(new CustomEvent('cart:refreshed'));
    } catch (e) {
      this.setModalError(this.config.addFailedText);
    } finally {
      if (this.addBothBtn) this.addBothBtn.disabled = false;
    }
  }

  renderDrawer(json) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(json, true, false);
    }
  }

  openDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  /**
   * The cart page reloads when a line is changed, which destroys the toast — the
   * add-on would disappear with no explanation. Stash the message so the next
   * page load can show it.
   */
  rememberNotice(message) {
    if (!message) return;
    try {
      // Stamped rather than cleared on a timer: a timer races the reload it is
      // meant to survive (a slow cart-page reload would drop the notice). The
      // stamp lets the next load decide whether the notice is still relevant.
      sessionStorage.setItem('athomeGateNotice', JSON.stringify({ message, at: Date.now() }));
    } catch (e) {
      /* private mode / storage disabled — the in-page toast still fires */
    }
  }

  flushRememberedNotice() {
    try {
      const raw = sessionStorage.getItem('athomeGateNotice');
      if (!raw) return;
      sessionStorage.removeItem('athomeGateNotice');
      const { message, at } = JSON.parse(raw);
      // Only surface a notice from the load that immediately followed the
      // removal, so it cannot reappear on an unrelated page later on.
      if (!message || !at || Date.now() - at > 15000) return;
      this.showToast(message);
    } catch (e) {
      /* ignore */
    }
  }

  showToast(message) {
    if (!message) return;
    let toast = document.querySelector('.athome-gate-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'athome-gate-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 5000);
  }
}

if (!customElements.get('at-home-addon-gate')) {
  customElements.define('at-home-addon-gate', AtHomeAddonGate);
}
